import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Add navigation
import api from "../utils/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Plus, Trash2, LayoutGrid, List, Edit, ArrowUpDown, ArrowUp, ArrowDown, Users, AlertCircle, CheckSquare, Scale, HelpCircle, X, Check, Search, Calendar as CalendarIcon, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import ConfirmDialog from "./ConfirmDialog";
import Pagination from "./Pagination";
import { useSettings } from "../contexts/SettingsContext";
import { formatDate, formatTime } from "../utils/formatters";
import { LoadingSkeleton } from "./LoadingSpinner";
import AddTeamMemberModal from "./AddTeamMemberModal"; // Import the new modal
import { GUIDE_CONTENT } from '../config/guideContent';
import DashboardPageLayout from './DashboardPageLayout';
import CardListViewToggle from './CardListViewToggle';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, DangerButton, SuccessButton } from './Buttons';
import ActionBar from './ActionBar';
import { useTeamMembers, useRoles } from '../hooks/useData';
import RoleSelector from './RoleSelector';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import TimeSlotSelector from './TimeSlotSelector';
import ScheduleEditor from './ScheduleEditor';
import { formatReadableDate, formatShortDate } from '../utils/dateFormatters';
import QuickAvailabilityModal from './QuickAvailabilityModal';
import TeamScheduleModal from './TeamScheduleModal';
import ResourceCard from './ResourceCard';

const DAYS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
];

const TeamMembersTab = ({ teamMembers, onRefresh }) => {
  const navigate = useNavigate(); // Add navigation hook
  const { dateFormat, timeFormat } = useSettings();
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState("card"); // Default to card view
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [showStepGuide, setShowStepGuide] = useState(() => {
    return localStorage.getItem('hideStepGuide') !== 'true';
  });

  // Listen for guide visibility changes
  useEffect(() => {
    const handleGuideChange = () => {
      setShowStepGuide(localStorage.getItem('hideStepGuide') !== 'true');
    };
    window.addEventListener('guideVisibilityChanged', handleGuideChange);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideChange);
  }, []);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [expandedTeamMembers, setExpandedTeamMembers] = useState({});
  const itemsPerPage = 12;
  
  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [quickAvailabilityOpen, setQuickAvailabilityOpen] = useState(false);
  const [selectedTeamForSchedule, setSelectedTeamForSchedule] = useState(null);
  
  // Role management with useSWR
  const { roles } = useRoles();
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [showAddModal, setShowAddModal] = useState(false); // New state for add modal

  // Fetch pending requests
  useEffect(() => {
    const fetchPendingRequests = async () => {
      try {
        const response = await api.get('/change-requests/pending');
        setPendingRequests(response.data || []);
      } catch (error) {
        console.error("Error fetching pending requests:", error);
      }
    };

    fetchPendingRequests();
    // Poll every 30 seconds
    const interval = setInterval(fetchPendingRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to check if teamMember has pending requests
  const hasPendingRequest = (teamMemberId) => {
    return pendingRequests.some(req => req.teamMemberId === teamMemberId && req.status === 'pending');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };
  
  // Mass action handlers
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedTeamMembers([]);
  };
  
  const toggleTeamMemberSelection = (teamMemberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(teamMemberId) 
        ? prev.filter(id => id !== teamMemberId)
        : [...prev, teamMemberId]
    );
  };
  
  const selectAllTeamMembers = () => {
    setSelectedTeamMembers(paginatedTeamMembers.map(teamMember => teamMember.id));
  };
  
  const deselectAllTeamMembers = () => {
    setSelectedTeamMembers([]);
  };
  
  const handleBulkDelete = async () => {
    try {
      const deletePromises = selectedTeamMembers.map(teamMemberId =>
        api.delete(`/team/${teamMemberId}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedTeamMembers.length} teamMember(s) deleted successfully`);
      setSelectedTeamMembers([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting teamMembers:", error);
      toast.error("Failed to delete some teamMembers");
      setBulkDeleteConfirm(false);
    }
  };
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [doubleDeckerCertified, setDoubleDeckerCertified] = useState(false);
  const [recurringAvailability, setRecurringAvailability] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [schedulerWeight, setSchedulerWeight] = useState(3); // Default priority
  const [formHasChanges, setFormHasChanges] = useState(false);

  // Auto-generate username from name
  const handleNameChange = (value) => {
    setName(value);
    
    // Only auto-generate if username hasn't been manually edited
    if (!usernameManuallyEdited && !editingTeamMember) {
      const generatedUsername = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') // Remove special chars and spaces
        .slice(0, 20); // Limit length
      setUsername(generatedUsername);
    }
  };

  const handleUsernameChange = (value) => {
    setUsername(value);
    setUsernameManuallyEdited(true);
  };
  const [currentDay, setCurrentDay] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const handleAddAvailability = () => {
    if (currentDay && startTime && endTime) {
      setRecurringAvailability([
        ...recurringAvailability,
        { day: currentDay, startTime, endTime }
      ]);
      setFormHasChanges(true);
      setCurrentDay("");
      setStartTime("");
      setEndTime("");
    }
  };

  const handleRemoveAvailability = (index) => {
    setRecurringAvailability(recurringAvailability.filter((_, i) => i !== index));
    setFormHasChanges(true);
  };

  const handleDateSelect = (selectedDates) => {
    // Calendar passes array or Set of Date objects
    if (!selectedDates || (Array.isArray(selectedDates) && selectedDates.length === 0)) {
      setUnavailableDates([]);
      setFormHasChanges(true);
      return;
    }
    
    // Convert to array
    const datesArray = Array.isArray(selectedDates) ? selectedDates : Array.from(selectedDates);
    
    // Convert Date objects to YYYY-MM-DD strings using LOCAL timezone
    const dateStrings = datesArray
      .filter(date => date instanceof Date && !isNaN(date.getTime()))
      .map(date => {
        // Use local timezone to prevent off-by-one errors
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      });
    
    setUnavailableDates(dateStrings);
    setFormHasChanges(true);
  };

  const handleEditClick = (teamMember) => {
    setEditingTeamMember(teamMember);
    setName(teamMember.name);
    setEmail(teamMember.email || "");
    setPhone(teamMember.phone || "");
    setUsername(teamMember.username || "");
    setPassword(""); // Don't show existing password
    setDoubleDeckerCertified(teamMember.doubleDeckerCertified);
    setRecurringAvailability(teamMember.recurringAvailability || []);
    setUnavailableDates(teamMember.unavailableDates || []);
    setSchedulerWeight(teamMember.schedulerWeight || 3); // Load scheduler priority
    setSelectedRoleId(teamMember.roleId || ""); // Load assigned role
    setOpen(true);
  };

  const handleQuickSelectAllDays = () => {
    const allDays = DAYS.map(day => ({
      day: day.value,
      startTime: "18:00", // 6 PM
      endTime: "22:00"    // 10 PM
    }));
    setRecurringAvailability(allDays);
    setFormHasChanges(true);
    toast.success("Added availability for all days (6 PM - 10 PM)");
  };

  const handleQuickSelectWeekdays = () => {
    const weekdays = DAYS.slice(0, 5).map(day => ({ // Monday-Friday
      day: day.value,
      startTime: "18:00",
      endTime: "22:00"
    }));
    setRecurringAvailability(weekdays);
    setFormHasChanges(true);
    toast.success("Added availability for weekdays (6 PM - 10 PM)");
  };

  const handleQuickSelectWeekends = () => {
    const weekends = DAYS.slice(5, 7).map(day => ({ // Saturday-Sunday
      day: day.value,
      startTime: "18:00",
      endTime: "22:00"
    }));
    setRecurringAvailability(weekends);
    setFormHasChanges(true);
    toast.success("Added availability for weekends (6 PM - 10 PM)");
  };

  const handleQuickSelectTimeSlot = (startTime, endTime, label) => {
    // Apply time slot to existing availability or create all days with this time
    if (recurringAvailability.length > 0) {
      const updated = recurringAvailability.map(avail => ({
        ...avail,
        startTime,
        endTime
      }));
      setRecurringAvailability(updated);
      setFormHasChanges(true);
      toast.success(`Updated all availability to ${label}`);
    } else {
      const allDays = DAYS.map(day => ({
        day: day.value,
        startTime,
        endTime
      }));
      setRecurringAvailability(allDays);
      setFormHasChanges(true);
      toast.success(`Added all days with ${label}`);
    }
  };

  const handleSubmit = async () => {
    console.log('🔧 handleSubmit called');
    console.log('Editing team member:', editingTeamMember);
    console.log('Form data:', { name, email, phone, username, password: password ? '***' : 'empty', selectedRoleId });
    
    if (!name.trim()) {
      toast.error("Please enter teamMember name");
      return;
    }

    // Role is required for new teamMembers, but can be optional for existing ones
    if (!editingTeamMember && !selectedRoleId) {
      toast.error("Please select a role for this teamMember");
      return;
    }

    if (!editingTeamMember && (!username || !password)) {
      toast.error("Please enter username and password for new teamMember");
      return;
    }

    try {
      // Find the selected role to get role name for caching
      const selectedRole = selectedRoleId ? roles.find(r => r.id === selectedRoleId) : null;
      
      const payload = {
        name: name.trim(),
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        username: editingTeamMember ? editingTeamMember.username : username?.trim(),
        roleId: selectedRoleId || null,
        roleName: selectedRole?.name || null,
        recurringAvailability: recurringAvailability || [],
        unavailableDates: unavailableDates || [],
        payRate: 20.0,
        schedulerWeight: schedulerWeight || 3,
        doubleDeckerCertified: doubleDeckerCertified || false
      };

      // Only include password if provided
      if (password?.trim()) {
        payload.password = password.trim();
      } else if (!editingTeamMember) {
        toast.error("Password is required for new teamMembers");
        return;
      }

      console.log('📤 Sending payload:', { ...payload, password: payload.password ? '***' : undefined });

      if (editingTeamMember) {
        console.log(`📡 PUT /team/${editingTeamMember.id}`);
        const response = await api.put(`/team/${editingTeamMember.id}`, payload);
        console.log('✅ Response:', response.data);
        toast.success("Team member updated successfully");
      } else {
        console.log('📡 POST /team');
        const response = await api.post('/team', payload);
        console.log('✅ Response:', response.data);
        toast.success("Team member added successfully");
      }
      
      setFormHasChanges(false);
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving teamMember:", error);
      
      // Parse backend validation errors
      let errorMessage = "Failed to save teamMember";
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      }
      
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const handleDeleteClick = (teamMember) => {
    setDeleteConfirm({ open: true, id: teamMember.id, name: teamMember.name });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/team/${deleteConfirm.id}`);
      toast.success("TeamMember deleted successfully");
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting teamMember:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete teamMember";
      
      // Show detailed error message for referential integrity violations
      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete teamMember");
      }
      
      setDeleteConfirm({ open: false, id: null, name: "" });
    }
  };

  const resetForm = () => {
    setEditingTeamMember(null);
    setName("");
    setEmail("");
    setPhone("");
    setUsername("");
    setPassword("");
    setDoubleDeckerCertified(false);
    setRecurringAvailability([]);
    setUnavailableDates([]);
    setCurrentDay("");
    setStartTime("");
    setEndTime("");
    setUsernameManuallyEdited(false);
    setSchedulerWeight(3); // Reset to default
    setSelectedRoleId(""); // Reset role selection
    setFormHasChanges(false);
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  // Sort teamMembers
  const sortedTeamMembers = [...teamMembers].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortField) {
      case "name":
        compareValue = a.name.localeCompare(b.name);
        break;
      case "roleName":
        compareValue = (a.roleName || '').localeCompare(b.roleName || '');
        break;
      case "schedulerWeight":
        compareValue = (a.schedulerWeight || 3) - (b.schedulerWeight || 3);
        break;
      case "availability":
        compareValue = (a.recurringAvailability?.length || 0) - (b.recurringAvailability?.length || 0);
        break;
      case "unavailable":
        compareValue = (a.unavailableDates?.length || 0) - (b.unavailableDates?.length || 0);
        break;
      default:
        compareValue = 0;
    }
    
    return sortDirection === "asc" ? compareValue : -compareValue;
  });

  // Pagination
  const totalPages = Math.ceil(sortedTeamMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeamMembers = sortedTeamMembers.slice(startIndex, startIndex + itemsPerPage);

  return (
    <DashboardPageLayout
      title="Team"
      subtitle="Build your team"
      guideChildren={
        showStepGuide && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full">
            <div className="flex-1">
              <div className="flex items-start sm:items-center gap-3">
                {teamMembers.length > 0 ? (
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    ✓
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    2
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">
                    {GUIDE_CONTENT.team.title}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    {GUIDE_CONTENT.team.dynamicDescription(teamMembers.length)}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowStepGuide(false);
                  localStorage.setItem('hideStepGuide', 'true');
                  window.dispatchEvent(new Event('guideVisibilityChanged'));
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Dismiss</span>
              </Button>
              {teamMembers.length > 0 && (
                <PrimaryButton 
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'assets' }))}
                  size="sm"
                >
                  Next: Add Assets →
                </PrimaryButton>
              )}
            </div>
          </div>
        )
      }
      actions={
        <div className="flex items-center gap-2 w-full">
          {/* Mass Actions - Icon only, same size as view toggle */}
          <Button
            onClick={toggleMassActionMode}
            variant="outline"
            size="sm"
            className={`h-9 w-9 p-0 flex items-center justify-center ${
              massActionMode ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
            }`}
          >
            <CheckSquare className="w-4 h-4" />
          </Button>
          
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          <PrimaryButton 
            data-testid="add-teamMember-button" 
            className="h-9 flex-1 sm:flex-none sm:px-4"
            onClick={() => setOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add
          </PrimaryButton>
        </div>
      }
    >
      <div data-testid="teamMembers-tab" className="space-y-6">
      <AddTeamMemberModal 
        isOpen={open && !editingTeamMember} 
        onClose={() => setOpen(false)} 
        onSuccess={onRefresh} 
      />
          
      {/* Edit TeamMember Modal - Tabbed for Organization */}
      {editingTeamMember && (
        <StandardModal
          isOpen={open}
          onClose={handleOpenChange}
          title={`Edit ${editingTeamMember.name}`}
          description="Update teamMember details"
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none">
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleSubmit} className="flex-1 sm:flex-none">
                Save Changes
              </PrimaryButton>
            </>
          }
        >
          <Tabs defaultValue="basic" className="w-full">
            <div className="mb-4">
              <TabsList className="grid w-full grid-cols-3 h-auto sm:h-11">
                <TabsTrigger value="basic" className="text-xs sm:text-sm py-2">Basic</TabsTrigger>
                <TabsTrigger value="role" className="text-xs sm:text-sm py-2">Role</TabsTrigger>
                <TabsTrigger value="availability" className="text-xs sm:text-sm py-2">Schedule</TabsTrigger>
              </TabsList>
            </div>
            
            {/* Tab 1: Basic Info */}
            <TabsContent value="basic" className="space-y-4 mt-6">
              <div>
                <Label htmlFor="teamMember-name">Name *</Label>
                <Input
                  id="teamMember-name"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Team member name"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="teamMember-email">Email</Label>
                  <Input
                    id="teamMember-email"
                    type="email"
                    value={email}
                    disabled={true}
                    placeholder="email@example.com"
                    className="mt-1 bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>
                  {email && !editingTeamMember?.emailVerified && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/team/${editingTeamMember.id}/resend-verification`);
                          toast.success('Verification email sent!');
                        } catch (error) {
                          toast.error('Failed to send email');
                        }
                      }}
                      className="text-xs text-blue-600 hover:underline font-medium mt-1"
                    >
                      Resend Verification Email
                    </button>
                  )}
                </div>
                <div>
                  <Label htmlFor="teamMember-phone">Phone</Label>
                  <Input
                    id="teamMember-phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={phone}
                    disabled={true}
                    placeholder="(555) 123-4567"
                    className="mt-1 bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="teamMember-username">
                    Username 
                    <span className="text-xs text-gray-500 ml-1">(cannot change)</span>
                  </Label>
                  <Input
                    id="teamMember-username"
                    value={username}
                    disabled={true}
                    className="bg-gray-100 mt-1 mb-2"
                  />
                </div>
                <div>
                  <Label htmlFor="teamMember-password">
                    Password 
                    <span className="text-xs text-gray-500 ml-1">(leave blank to keep)</span>
                  </Label>
                  <Input
                    id="teamMember-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password"
                    className="mt-1 mb-2"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Role & Priority */}
            <TabsContent value="role" className="space-y-4 mt-6">
              <div>
                <Label>Role *</Label>
                <RoleSelector
                  roles={roles}
                  value={selectedRoleId}
                  onChange={setSelectedRoleId}
                  placeholder="Select a role..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Priority</Label>
                <Select 
                  value={String(schedulerWeight || 3)} 
                  onValueChange={(value) => setSchedulerWeight(parseInt(value))}
                >
                  <SelectTrigger className="w-full mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">High</SelectItem>
                    <SelectItem value="3">Normal</SelectItem>
                    <SelectItem value="1">Low</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Higher priority gets more trips from auto-scheduler
                </p>
              </div>
            </TabsContent>

            {/* Tab 3: Schedule (Availability) */}
            <TabsContent value="availability" className="mt-6">
              <ScheduleEditor
                value={recurringAvailability}
                onChange={(newSchedule) => {
                  setRecurringAvailability(newSchedule);
                  setFormHasChanges(true);
                }}
              />
            </TabsContent>
          </Tabs>
        </StandardModal>
      )}
      
      {/* Mass Action Toolbar */}
      {massActionMode && (
        <MassActionToolbar
          selectedCount={selectedTeamMembers.length}
          onSelectAll={selectAllTeamMembers}
          onDeselectAll={deselectAllTeamMembers}
          onDelete={() => setBulkDeleteConfirm(true)}
          itemName="teamMember"
        />
      )}

      {!teamMembers ? (
        <LoadingSkeleton type="card" count={6} />
      ) : teamMembers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No TeamMembers Added Yet"
          description="Click 'Add New Team' to get started"
          actionLabel="Add New Team"
          onAction={() => setOpen(true)}
        />
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedTeamMembers.map((teamMember) => {
            const isSelected = selectedTeamMembers.includes(teamMember.id);
            const roleColor = roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6';
            
            // Build expanded content JSX
            const expandedContent = (
              <>
                {/* Email Verification Status */}
                {!teamMember.emailVerified && teamMember.email && (
                  <div className="mb-3">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-yellow-800 mb-2">⚠️ Email Not Verified</p>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/team/${teamMember.id}/resend-verification`);
                            toast.success('Verification email sent!');
                          } catch (error) {
                            toast.error('Failed to send email');
                          }
                        }}
                        className="text-xs text-blue-600 hover:underline font-medium"
                      >
                        Resend Verification Email →
                      </button>
                    </div>
                  </div>
                )}

                {/* Priority Badge */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Priority</p>
                  <Badge 
                    className={`text-xs ${
                      teamMember.schedulerWeight === 5 ? 'bg-green-100 text-green-800' :
                      teamMember.schedulerWeight === 4 ? 'bg-blue-100 text-blue-800' :
                      teamMember.schedulerWeight === 3 ? 'bg-gray-100 text-gray-800' :
                      teamMember.schedulerWeight === 2 ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}
                  >
                    {teamMember.schedulerWeight === 5 ? 'High' :
                     teamMember.schedulerWeight === 4 ? 'High' :
                     teamMember.schedulerWeight === 3 ? 'Normal' :
                     teamMember.schedulerWeight === 2 ? 'Low' :
                     'Low'} Priority
                  </Badge>
                </div>
                
                {/* Availability Schedule */}
                {teamMember.recurringAvailability?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Schedule</p>
                    <div className="space-y-1">
                      {teamMember.recurringAvailability.map((avail, idx) => (
                        <div key={idx} className="flex justify-between text-xs text-gray-600 bg-gray-50 p-2 rounded">
                          <span className="font-medium">{DAYS.find(d => d.value === avail.day)?.label}</span>
                          <span>{formatTime(avail.startTime, timeFormat)} - {formatTime(avail.endTime, timeFormat)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unavailable Dates */}
                {teamMember.unavailableDates?.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-red-700 mb-2">Unavailable Dates</p>
                    <div className="flex flex-wrap gap-1">
                      {teamMember.unavailableDates.slice(0, 6).map(date => (
                        <Badge key={date} className="bg-red-100 text-red-800 text-xs">
                          {formatShortDate(date)}
                        </Badge>
                      ))}
                      {teamMember.unavailableDates.length > 6 && (
                        <span className="text-xs text-red-600">+{teamMember.unavailableDates.length - 6} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* No schedule/dates info */}
                {(!teamMember.recurringAvailability || teamMember.recurringAvailability.length === 0) && 
                 (!teamMember.unavailableDates || teamMember.unavailableDates.length === 0) && (
                  <div className="text-center py-4 text-xs text-gray-500">
                    No schedule or unavailable dates set
                  </div>
                )}
              </>
            );
            
            return (
              <ResourceCard
                key={teamMember.id}
                id={teamMember.id}
                name={teamMember.name}
                icon={Users}
                iconColor={roleColor}
                primaryBadge={
                  // Show "Unverified" in gray if email not verified, otherwise show role
                  teamMember.email && !teamMember.emailVerified ? {
                    label: 'Unverified',
                    color: '#6B7280',
                    variant: 'outline',
                    className: 'text-xs mb-2'
                  } : teamMember.roleName ? {
                    label: teamMember.roleName,
                    color: roleColor,
                    variant: 'outline',
                    className: 'text-xs mb-2'
                  } : null
                }
                details={[
                  ...(teamMember.email ? [{ icon: <Mail className="w-3 h-3" />, text: teamMember.email }] : []),
                  ...(teamMember.phone ? [{ icon: <Phone className="w-3 h-3" />, text: teamMember.phone }] : [])
                ]}
                additionalBadges={hasPendingRequest(teamMember.id) && (
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 flex items-center gap-1 text-xs mt-2 w-fit">
                    <AlertCircle className="w-3 h-3" />
                    Pending
                  </Badge>
                )}
                showSeeMore={true}
                expandedContent={expandedContent}
                isExpanded={expandedTeamMembers[teamMember.id]}
                onToggleExpand={() => {
                  const allExpanded = Object.keys(expandedTeamMembers).length === paginatedTeamMembers.length && 
                                     Object.values(expandedTeamMembers).every(val => val);
                  
                  if (allExpanded) {
                    setExpandedTeamMembers({});
                  } else {
                    const newExpanded = {};
                    paginatedTeamMembers.forEach(c => {
                      newExpanded[c.id] = true;
                    });
                    setExpandedTeamMembers(newExpanded);
                  }
                }}
                leftBorderColors={[roleColor]}
                massActionMode={massActionMode}
                isSelected={isSelected}
                onToggleSelect={() => toggleTeamMemberSelection(teamMember.id)}
                additionalClassNames={hasPendingRequest(teamMember.id) ? 'ring-2 ring-orange-400' : ''}
                onEdit={() => handleEditClick(teamMember)}
                onDelete={() => handleDeleteClick(teamMember)}
              />
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[768px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {massActionMode && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedTeamMembers.length === paginatedTeamMembers.length && paginatedTeamMembers.length > 0}
                        onChange={(e) => e.target.checked ? selectAllTeamMembers() : deselectAllTeamMembers()}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button 
                      onClick={() => handleSort("name")}
                      className="flex items-center hover:text-gray-700 focus:outline-none"
                    >
                      Name
                      {getSortIcon("name")}
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button 
                      onClick={() => handleSort("roleName")}
                      className="flex items-center hover:text-gray-700 focus:outline-none"
                    >
                      <Users className="w-3 h-3 mr-1" />
                      Role
                      {getSortIcon("roleName")}
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button 
                      onClick={() => setQuickAvailabilityOpen(true)}
                      className="flex items-center hover:text-blue-600 focus:outline-none transition-colors"
                    >
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      Availability
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    <button 
                      onClick={() => handleSort("schedulerWeight")}
                      className="flex items-center hover:text-gray-700 focus:outline-none"
                    >
                      <Scale className="w-3 h-3 mr-1" />
                      Priority
                      {getSortIcon("schedulerWeight")}
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedTeamMembers.map((teamMember) => {
                  const isSelected = selectedTeamMembers.includes(teamMember.id);
                  return (
                  <tr key={teamMember.id} className={`hover:bg-gray-50 ${hasPendingRequest(teamMember.id) ? 'bg-orange-50' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
                    {massActionMode && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTeamMemberSelection(teamMember.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs sm:text-sm font-medium text-gray-900">{teamMember.name}</span>
                            {hasPendingRequest(teamMember.id) && (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] px-1.5 py-0 flex items-center gap-1">
                                <AlertCircle className="w-2.5 h-2.5" />
                                Pending
                              </Badge>
                            )}
                          </div>
                          {(teamMember.email || teamMember.phone) && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {teamMember.phone && <div>{teamMember.phone}</div>}
                              {teamMember.email && <div className="truncate max-w-[150px]">{teamMember.email}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    {/* Role Column */}
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      {teamMember.roleName ? (
                        <Badge 
                          className="text-xs"
                          style={{ 
                            backgroundColor: roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6',
                            color: 'white'
                          }}
                        >
                          {teamMember.roleName}
                        </Badge>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No role assigned</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <SecondaryButton
                        size="sm"
                        onClick={() => setSelectedTeamForSchedule(teamMember)}
                        className="text-xs"
                      >
                        View Schedule
                      </SecondaryButton>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-center">
                        <span className={`
                          px-2 py-1 rounded-full text-xs font-semibold
                          ${teamMember.schedulerWeight === 5 ? 'bg-green-100 text-green-700' : ''}
                          ${teamMember.schedulerWeight === 4 ? 'bg-blue-100 text-blue-700' : ''}
                          ${teamMember.schedulerWeight === 3 ? 'bg-gray-100 text-gray-700' : ''}
                          ${teamMember.schedulerWeight === 2 ? 'bg-orange-100 text-orange-700' : ''}
                          ${teamMember.schedulerWeight === 1 ? 'bg-red-100 text-red-700' : ''}
                        `}>
                          {teamMember.schedulerWeight === 5 ? 'High' :
                           teamMember.schedulerWeight === 4 ? 'High' :
                           teamMember.schedulerWeight === 3 ? 'Normal' :
                           teamMember.schedulerWeight === 2 ? 'Low' : 'Low'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium">
                      <div className="flex justify-end gap-1 sm:gap-2">
                        <Button
                          data-testid={`edit-teamMember-list-${teamMember.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(teamMember)}
                          className="text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-teamMember-list-${teamMember.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(teamMember)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {teamMembers && sortedTeamMembers.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={sortedTeamMembers.length}
        />
      )}

      {/* Delete TeamMember Modal */}
      <StandardModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
        title="Delete TeamMember?"
        description={`Are you sure you want to delete ${deleteConfirm.name}?`}
        size="sm"
        footer={
          <>
            <SecondaryButton
              onClick={() => setDeleteConfirm({ open: false, id: null, name: "" })}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleDeleteConfirm}
              className="flex-1"
            >
              Delete TeamMember
            </DangerButton>
          </>
        }
      >
        <InfoCard variant="danger">
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>This teamMember will be unassigned from all future trips.</p>
        </InfoCard>
      </StandardModal>
      
      {/* Bulk Delete Modal */}
      <StandardModal
        isOpen={bulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirm(false)}
        title="Delete Multiple TeamMembers?"
        description={`Are you sure you want to delete ${selectedTeamMembers.length} teamMember(s)?`}
        size="sm"
        footer={
          <>
            <SecondaryButton
              onClick={() => setBulkDeleteConfirm(false)}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleBulkDelete}
              className="flex-1"
            >
              Delete {selectedTeamMembers.length} TeamMembers
            </DangerButton>
          </>
        }
      >
        <InfoCard variant="danger">
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>All selected teamMembers will be unassigned from future trips.</p>
        </InfoCard>
      </StandardModal>
      
      {/* Quick Availability Check Modal */}
      <QuickAvailabilityModal
        isOpen={quickAvailabilityOpen}
        onClose={() => setQuickAvailabilityOpen(false)}
        teamMembers={teamMembers}
        roles={roles}
      />
      
      {/* Individual Team Schedule Modal */}
      <TeamScheduleModal
        isOpen={!!selectedTeamForSchedule}
        onClose={() => setSelectedTeamForSchedule(null)}
        teamMember={selectedTeamForSchedule}
        timeFormat={timeFormat}
      />
      </div>
    </DashboardPageLayout>
  );
};

export default TeamMembersTab;